import { classifySourceState, normalizeStatus } from './issues/status';
import { activityReferenceDay, classifyPermitActivity } from './presentation';
import { recordThread } from './property-feed';
import { isPermit, resolvedDespiteCityStatus, type DisplayRecord } from './property-records';
import { documentTime } from './record-dates';

/** Summary cost policy, independent of where a record is grouped in the timeline. */
export function eligibleForAiSummary(record: DisplayRecord, asOf: string): boolean {
  const state = classifySourceState({
    recordKind: record.kind,
    status: record.status,
    closedAt: record.closedAt,
  });
  // An old issued permit can be historical without being closed. Conversely,
  // expired permits must not inherit the case graph's "unfinished work" state.
  const pendingPermit =
    isPermit(record.kind) &&
    !record.closedAt &&
    ['approved', 'filing', 'triage', 'plan filed', 'plan check'].includes(
      normalizeStatus(record.status),
    );
  const open =
    record.kind === 'recorder'
      ? Boolean(record.lien && !record.lien.released)
      : !resolvedDespiteCityStatus(record) && (state === 'open' || pendingPermit);
  if (open) return true;
  const reference = activityReferenceDay(asOf);
  if (reference === null) return false;
  const cutoff = new Date(reference);
  cutoff.setUTCHours(0, 0, 0, 0);
  const month = cutoff.getUTCMonth();
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 3);
  if (cutoff.getUTCMonth() !== month) cutoff.setUTCDate(0);
  const dates = [
    record.date,
    record.closedAt,
    ...(isPermit(record.kind) ? [classifyPermitActivity(record, asOf)?.lastActivityAt] : []),
    // Typed history includes stage Action Date and review events that are not
    // always present in the listing's latest date. An actual expiration stage
    // is activity; a planned expiry field or inspection appointment is not.
    ...recordThread(record)
      .filter((event) => !/schedul|appointment/i.test(`${event.label} ${event.status}`))
      .map((event) => event.date),
  ];
  return dates.some((value) => {
    const time = documentTime(value);
    if (time === null) return false;
    const day = new Date(time).setUTCHours(0, 0, 0, 0);
    return day >= cutoff.getTime() && day <= reference;
  });
}
